// src/components/PurchaseFlow/Step/DocumentUploadStep.js
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile, 
  faIdCard, 
  faIdCardAlt, 
  faFileInvoiceDollar, 
  faQrcode,
  faMobileScreen,
  faUserCircle,
  faSmile,
  faIdBadge
} from '@fortawesome/free-solid-svg-icons';
import NavigationButtons from '../../NavigationButtons';
import DocumentItem from '../../../DocumentItem/PendingDocumentItem';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../../services/api';
import { TokenService } from '../../../../services/tokenService';
import { trackDocumentUploaded } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import './DocumentUploadStep.css';

// Documents mapping
const DOCUMENTS_MAP = {
  documentFront: { name: 'DNI Frente', icon: faIdCard },
  documentBack: { name: 'DNI Dorso', icon: faIdCardAlt },
  bankStatement: { name: 'Extracto de Cuenta', icon: faFileInvoiceDollar },
  paycheck: { name: 'Recibo de Sueldo', icon: faFileInvoiceDollar },
  selfie: { name: 'Foto Selfie', icon: faUserCircle },
  selfieSmiling: { name: 'Foto Selfie Sonriendo', icon: faSmile },
  selfieDocument: { name: 'Foto Selfie con Documento', icon: faIdBadge },
  service: { name: 'Servicio', icon: faFileInvoiceDollar },
};

const MySwal = withReactContent(Swal);

const DocumentUploadStep = ({ onNext, onBack, clientData, purchaseRequestId }) => {
  const [documents, setDocuments] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [temporaryToken, setTemporaryToken] = useState(null);
  const [progress, setProgress] = useState(0);
  const [qrUrl, setQrUrl] = useState(null);
  const [pollingTimeout, setPollingTimeout] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [qrCanvas, setQrCanvas] = useState(null);
  const { commerceId, branchId } = useAnalyticsContext();
  
  // Configuración para diferimiento de documentación (simplificada: todos o ninguno)
  const allowDeferredDocumentation = process.env.REACT_APP_ALLOW_DEFERED_DOCUMENTATION === 'true';

  // Generar token temporal y URL para QR
  useEffect(() => {
    const generateTemporaryToken = async () => {
      try {
        const response = await TokenService.generateOnboardingToken(
          clientData.id,
          purchaseRequestId,
          30
        );
        
        const { token } = response.data;
        const mobileUrl = TokenService.generateMobileUrl(token, 'onboarding');
        console.log('mobileUrl', mobileUrl);
        setTemporaryToken(token);
        setQrUrl(mobileUrl);
        
        // Generar QR
        if (mobileUrl) {
          QRCode.toCanvas(document.getElementById('qr-canvas'), mobileUrl, {
            width: 150,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          });
        }
      } catch (error) {
        console.error('Error al generar token temporal:', error);
        MySwal.fire({
          title: 'Error',
          text: 'No se pudo generar el acceso móvil',
          icon: 'error'
        });
      }
    };

    generateTemporaryToken();
  }, [clientData.id, purchaseRequestId]);

  // Estados posibles de documentos:
  // pending: no hay archivo
  // selected: archivo seleccionado localmente pero no subido al servidor
  // uploaded: archivo subido al servidor (desde móvil o después de "Continuar")
  const handleFileUpload = (docType, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setDocuments((prevDocs) => ({
        ...prevDocs,
        [docType]: {
          ...prevDocs[docType],
          file,
          preview: reader.result,
          status: 'selected',
          uploadedFromMobile: false
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleReject = (docType) => {
    setDocuments((prevDocs) => ({
      ...prevDocs,
      [docType]: { ...prevDocs[docType], file: null, preview: null, status: 'pending' },
    }));
  };

  const startPolling = useCallback(() => {
    const timeout = setTimeout(() => {
      const checkDocuments = async () => {
        try {
          const response = await api.get(
            `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/documents`
          );
          
          setDocuments(prevDocuments => {
            const updatedDocuments = { ...prevDocuments };
            const newDocuments = [];

            response.data.forEach(serverDoc => {
              if (updatedDocuments[serverDoc.type]) {
                const currentDoc = updatedDocuments[serverDoc.type];
                
                // Solo actualizamos si no está ya subido o si fue subido desde el móvil
                if (serverDoc.status === 'uploaded' && 
                    (currentDoc.status === 'pending' || 
                     currentDoc.status === 'selected')) {
                  
                  updatedDocuments[serverDoc.type] = {
                    ...currentDoc,
                    status: 'uploaded',
                    preview: serverDoc.file.url,
                    uploadedFromMobile: true,
                    title: serverDoc.title,
                    file: null // Limpiamos el archivo local si existe
                  };
                  
                  // Track document uploaded from mobile
                  if (commerceId) {
                    trackDocumentUploaded({
                      commerceId,
                      branchId,
                      documentCategory: serverDoc.type,
                      customerDni: clientData?.dni,
                      uploadedFromMobile: true
                    });
                  }
                  
                  newDocuments.push(DOCUMENTS_MAP[serverDoc.type]?.name || serverDoc.type);
                }
              }
            });

            if (newDocuments.length > 0) {
              MySwal.fire({
                icon: 'success',
                title: '¡Nuevos documentos!',
                text: `El cliente ha subido: ${newDocuments.join(', ')}`,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
              });
            }

            return newDocuments.length > 0 ? updatedDocuments : prevDocuments;
          });
        } catch (error) {
          console.error('Error verificando documentos:', error);
        }
      };

      checkDocuments();
      const interval = setInterval(checkDocuments, 10000);
      setPollingInterval(interval);
    }, 1); // 180000);

    setPollingTimeout(timeout);
  }, [clientData.id, purchaseRequestId]);

  // Inicializar documentos
  useEffect(() => {
    const requiredDocs = process.env.REACT_APP_FLOW_NEW_REQUIRED_DOCS.split(',');
    const optionalDocs = process.env.REACT_APP_FLOW_NEW_OPTIONAL_DOCS.split(',');
    const initialDocuments = {};

    requiredDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        file: null, 
        preview: null, 
        status: 'pending', 
        optional: false 
      };
    });

    optionalDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        file: null, 
        preview: null, 
        status: 'pending', 
        optional: true 
      };
    });

    setDocuments(initialDocuments);
  }, []); // Solo se ejecuta una vez al montar

  // Iniciar polling
  useEffect(() => {
    startPolling();
  }, [startPolling]);

  // Cleanup del polling
  useEffect(() => {
    return () => {
      if (pollingTimeout) clearTimeout(pollingTimeout);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingTimeout, pollingInterval]);

  useEffect(() => {
    if (qrUrl) {
      QRCode.toCanvas(document.getElementById('qr-canvas'), qrUrl, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
    }
  }, [qrUrl]);

  const allRequiredUploaded = () => {
    return Object.values(documents).every(doc => 
      doc.optional || 
      doc.status === 'uploaded' || 
      doc.status === 'selected' // Consideramos también los archivos seleccionados
    );
  };

  // Verifica si se puede proceder - el botón "Continuar" siempre está habilitado
  // La validación real ocurre en handleNext()
  const canProceed = () => {
    return true; // Siempre permitir hacer clic en "Continuar" para que se ejecute la validación
  };

  // Obtiene la lista de documentos pendientes que se pueden diferir (simplificado)
  const getDeferrableDocuments = () => {
    return Object.entries(documents)
      .filter(([docType, doc]) => 
        !doc.optional && 
        doc.status === 'pending'
      )
      .map(([docType, _]) => DOCUMENTS_MAP[docType]?.name || docType);
  };

  const handleNext = async () => {
    // Siempre validar que todos los documentos requeridos estén subidos
    if (!allRequiredUploaded()) {
      const deferrableList = getDeferrableDocuments();
      
      MySwal.fire({
        title: 'Documentos requeridos pendientes',
        html: `
          <div class="text-start">
            <p class="mb-3 text-muted">Los siguientes documentos son requeridos para continuar:</p>
            <ul class="list-unstyled">
              ${deferrableList.map(doc => `<li class="mb-2 d-flex align-items-center"><i class="bi bi-exclamation-triangle text-warning me-2"></i><span>${doc}</span></li>`).join('')}
            </ul>
            ${allowDeferredDocumentation ? 
              `<div class="border-top pt-3 mt-3">
                <div class="d-flex align-items-start">
                  <i class="bi bi-lightbulb text-primary me-2 mt-1 flex-shrink-0"></i>
                  <div class="text-muted small">
                    Si deseas proceder sin estos documentos, puedes usar el botón <strong>"Cargar después"</strong> para diferir la documentación.
                  </div>
                </div>
              </div>` : 
              `<div class="border-top pt-3 mt-3">
                <div class="d-flex align-items-start">
                  <i class="bi bi-info-circle text-warning me-2 mt-1 flex-shrink-0"></i>
                  <div class="text-muted small">
                    <strong>Importante:</strong> Todos los documentos requeridos deben ser subidos para continuar.
                  </div>
                </div>
              </div>`
            }
          </div>
        `,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#6f42c1',
        customClass: {
          popup: 'swal2-popup-custom'
        }
      });
      return;
    }

    // Verificamos archivos pendientes de subir desde la computadora
    const pendingUploads = Object.entries(documents)
      .filter(([_, doc]) => doc.status === 'selected')
      .map(([key, _]) => key);

    if (pendingUploads.length === 0) {
      // No hay archivos para subir, todos fueron subidos desde el móvil
      onNext();
      return;
    }

    // Si hay archivos para subir, procedemos con la carga
    try {
      setIsUploading(true);
      setProgress(0);

      const formData = new FormData();
      pendingUploads.forEach(docType => {
        const doc = documents[docType];
        formData.append('files[]', doc.file);
        formData.append('categories[]', docType);
      });

      const response = await api.post(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/documents`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (event) => {
            const progressValue = Math.round((event.loaded / event.total) * 100);
            setProgress(progressValue);
          },
        }
      );

      // Track documents uploaded from desktop
      if (commerceId && response.data) {
        // The API might return uploaded documents info
        // Track each document category that was uploaded
        pendingUploads.forEach(docType => {
          trackDocumentUploaded({
            commerceId,
            branchId,
            documentCategory: docType,
            customerDni: clientData?.dni,
            uploadedFromMobile: false
          });
        });
      }

      onNext();
    } catch (error) {
      console.error('Error uploading documents:', error);
      setErrorMessage('Hubo un problema al cargar los documentos. Por favor, vuelve a intentarlo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipDocumentation = () => {
    const deferrableList = getDeferrableDocuments();
    
    if (deferrableList.length === 0) {
      // No hay documentos pendientes por diferir
      onNext();
      return;
    }

    MySwal.fire({
      title: '¿Continuar sin documentación?',
      html: `
        <div class="text-start">
          <p class="mb-3 text-muted">Los siguientes documentos quedarán pendientes:</p>
          <ul class="list-unstyled">
            ${deferrableList.map(doc => `<li class="mb-2 d-flex align-items-center"><i class="bi bi-exclamation-triangle text-warning me-2"></i><span>${doc}</span></li>`).join('')}
          </ul>
          <div class="border-top pt-3 mt-3">
            <div class="d-flex align-items-start">
              <i class="bi bi-info-circle text-warning me-2 mt-1 flex-shrink-0"></i>
              <div class="text-muted small">
                <strong>Importante:</strong> Esta documentación deberá ser subida posteriormente para completar y validar la venta.
              </div>
            </div>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#435ebe', // Bootstrap "primary"
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary', // large width btn
        cancelButton: 'btn btn-secondary', // make cancel button large and full width
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onNext();
      }
    });
  };

  const handleShareMobileAccess = () => {
    if (!qrUrl) return;

    MySwal.fire({
      title: 'Subir documentos desde el celular',
      html: `
        <div class="text-center">
          <canvas id="modal-qr-canvas" class="mb-4"></canvas>
          <p class="mb-3">El cliente puede escanear este código QR para subir los documentos desde su celular.</p>
          
          <div class="alert alert-warning d-flex align-items-center gap-2 mb-4" style="font-size: 0.9rem;">
            <div class="container-fluid">
              <div class="d-flex align-items-center gap-2">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div class="text-start">
                  Este link expirará en 30 minutos.
                </div>
              </div>
            </div>
          </div>

          <hr />
          <div class="row g-3">
            <div class="col-6">
              <p class="text-muted small mb-2">Copia este enlace:</p>
              <div class="input-group">
                <input 
                  type="text" 
                  value="${qrUrl}" 
                  class="form-control form-control-sm"
                  readonly
                />
                <button 
                  class="btn btn-outline-secondary copy-button"
                  type="button"
                  style="border: 1px solid #dce7f1 !important"
                  onclick="copyToClipboard('${qrUrl}', this)"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
            <div class="col-6">
              <p class="text-muted small mb-2">O comparte con el cliente:</p>
              <a 
                href="https://wa.me/?text=${encodeURIComponent(
                  `Puedes subir tus documentos desde aquí: ${qrUrl}`
                )}" 
                target="_blank" 
                class="btn btn-success w-100"
                style="background-color: #25D366; border-color: #25D366"
              >
                <i class="bi bi-whatsapp me-2"></i>
                Enviar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: () => {
        // Generar QR en el modal
        QRCode.toCanvas(document.getElementById('modal-qr-canvas'), qrUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });

        window.copyToClipboard = (text, button) => {
          navigator.clipboard.writeText(text).then(() => {
            const originalContent = button.innerHTML;
            button.innerHTML = '<i class="bi bi-check"></i>';
            button.disabled = true;
            setTimeout(() => {
              button.innerHTML = originalContent;
              button.disabled = false;
            }, 2000);
          });
        };
      }
    });
  };

  // Modificamos el renderizado del DocumentItem para mostrar el preview
  const renderDocumentItem = (key, doc) => (
    <DocumentItem
      key={key}
      icon={DOCUMENTS_MAP[key]?.icon || faFile}
      name={DOCUMENTS_MAP[key]?.name || key}
      optional={doc.optional}
      status={doc.status}
      onFileUpload={(file) => handleFileUpload(key, file)}
      filePreview={doc.preview}
      onReject={() => handleReject(key)}
      isUploading={isUploading}
      uploadedFromMobile={doc.uploadedFromMobile}
      onPreviewClick={() => {
        if (doc.preview) {
          MySwal.fire({
            title: doc.title || DOCUMENTS_MAP[key]?.name || key,
            imageUrl: doc.preview,
            imageAlt: 'Vista previa del documento',
            width: '80%',
            confirmButtonText: 'Cerrar'
          });
        }
      }}
    />
  );

  return (
    <div id="documentUploadStep" className="container my-5">
      <div className="position-relative mb-4">
        <h4 className="text-center mb-0">Carga de Documentos</h4>
        {allowDeferredDocumentation && getDeferrableDocuments().length > 0 && (
          <button 
            className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y"
            onClick={handleSkipDocumentation}
            disabled={isUploading}
            type="button"
          >
            <i className="bi bi-arrow-right ms-1"></i>
            {' '} Completar luego
          </button>
        )}
      </div>

      <div className="row justify-content-center align-items-start">
        {/* Columna Documentos */}
        <div className="col-md-8">
          <h5>Desde tu computadora</h5>
          <p className="text-muted small mb-4">
            Arrastra y suelta los documentos en las áreas correspondientes.
          </p>

          <ul className="document-list">
            {Object.entries(documents).map(([key, doc]) => 
              renderDocumentItem(key, doc)
            )}
          </ul>
        </div>

        {/* Columna QR */}
        <div className="col-md-4">
          <div className="qr-section text-center">
            <div className="card">
              <div className="card-body p-1">
                <FontAwesomeIcon icon={faMobileScreen} className="display-4 text-primary mb-3" />
                <h5>¿Prefieres usar el celular?</h5>
                <p className="text-muted small mb-4">
                  Escanea el código QR para subir los documentos desde tu teléfono
                </p>
                
                {qrUrl && (
                  <div className="qr-container mb-3">
                    <canvas id="qr-canvas" className="mb-3"></canvas>
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleShareMobileAccess}
                    >
                      <i className="bi bi-share me-2"></i>
                      Compartir acceso
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isUploading && (
        <>
          <div className="progress mt-3">
            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
              {progress}%
            </div>
          </div>
          <span>Subiendo documentos...</span>
        </>
      )}

      {errorMessage && (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {errorMessage}
        </div>
      )}

      {/* Botones de navegación */}
      <div className="d-flex justify-content-end align-items-center mt-4">
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!canProceed() || isUploading}
        >
          {isUploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Subiendo...
            </>
          ) : (
            <>
              Continuar
              <i className="bi bi-arrow-right ms-2"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentUploadStep;
